import React, { useEffect, useMemo, useState } from "react";
import { Input } from "@/components/ui/input";
import { useLang } from "@/i18n";

// Alle ISO 3166-1 alpha-2 codes; namen komen uit de browser (Intl.DisplayNames) in de taal van de gebruiker.
const CODES = ("AF AL DZ AD AO AR AM AU AT AZ BS BH BD BB BY BE BZ BJ BT BO BA BW BR BN BG BF BI KH CM CA CV CF TD CL CN CO KM CG CD CR CI HR CU CY CZ DK DJ DM DO EC EG SV GQ ER EE SZ ET FJ FI FR GA GM GE DE GH GR GD GT GN GW GY HT HN HU IS IN ID IR IQ IE IL IT JM JP JO KZ KE KI KP KR KW KG LA LV LB LS LR LY LI LT LU MG MW MY MV ML MT MH MR MU MX FM MD MC MN ME MA MZ MM NA NR NP NL NZ NI NE NG MK NO OM PK PW PA PG PY PE PH PL PT QA RO RU RW KN LC VC WS SM ST SA SN RS SC SL SG SK SI SB SO ZA SS ES LK SD SR SE CH SY TW TJ TZ TH TL TG TO TT TN TR TM TV UG UA AE GB US UY UZ VU VA VE VN YE ZM ZW XK GI JE GG IM FO GL HK MO PR AW CW").split(" ");
const FAVORITES = ["ES", "BE", "NL", "DE", "FR", "GB", "NO", "DK", "SE", "FI", "IT", "PT", "AT", "CH", "IE", "PL", "CZ", "LU"];

const strip = (s) => (s || "").normalize("NFKD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();

export function useCountryNames() {
  const { lang } = useLang();
  return useMemo(() => {
    let dn;
    try { dn = new Intl.DisplayNames([lang], { type: "region" }); } catch { dn = null; }
    const map = {};
    CODES.forEach((c) => { let n; try { n = dn ? dn.of(c) : c; } catch { n = c; } map[c] = n || c; });
    return map;
  }, [lang]);
}

/** Landkeuze: typ een naam of code; bewaart de ISO alpha-2 code (bv. "BE"). Compatibel met onChange(e). */
export function CountryField({ value, onChange, testid, className = "", placeholder, required = false, invalid }) {
  const names = useCountryNames();
  const [text, setText] = useState("");
  const listId = `countries-${testid || "list"}`;

  useEffect(() => { setText(value && names[value] ? `${names[value]} (${value})` : (value || "")); }, [value, names]);

  const options = useMemo(() => {
    const fav = FAVORITES.map((c) => ({ c, n: names[c] }));
    const rest = CODES.filter((c) => !FAVORITES.includes(c)).map((c) => ({ c, n: names[c] })).sort((a, b) => a.n.localeCompare(b.n));
    return [...fav, ...rest];
  }, [names]);

  const resolve = (t) => {
    const s = strip(t);
    if (!s) return "";
    const m = s.match(/\(([a-z]{2})\)\s*$/);
    if (m && names[m[1].toUpperCase()]) return m[1].toUpperCase();
    if (s.length === 2 && names[s.toUpperCase()]) return s.toUpperCase();
    const hit = options.find((o) => strip(o.n) === s);
    return hit ? hit.c : "";
  };

  const commit = (t) => {
    setText(t);
    const code = resolve(t);
    onChange({ target: { value: code || t } });
  };

  const ok = !text || !!resolve(text);
  return (
    <>
      <Input list={listId} required={required} data-invalid={invalid} value={text} onChange={(e) => commit(e.target.value)} onBlur={() => { const c = resolve(text); if (c) setText(`${names[c]} (${c})`); }}
        placeholder={placeholder} autoComplete="off" className={`${className} ${ok ? "" : "border-rose-400"}`} data-testid={testid} data-country={value || ""} />
      <datalist id={listId}>{options.map((o) => <option key={o.c} value={`${o.n} (${o.c})`} />)}</datalist>
    </>
  );
}
